// appels: what a failed call means. Pure, so it is tested in plain Node.
import { z } from "zod";
import { isApiError } from "@core/errors";

/**
 * Why an appel call failed (appels-v1.md, checked live on 4 Nov 2026):
 *   - `400 validation_error` with `fields: { closing_at: [{ code: "closing_before_opening" }] }`:
 *     the codes sit under `fields`, keyed by the field, one object per code.
 *   - `409 name_taken`: another appel of the cohort already uses this name.
 *   - `409 appel_revision_conflict`: someone saved the appel since it was loaded; reload.
 *   - `409 appel_not_editable`: the appel is no longer a draft.
 *   - `409 cohort_archived`: the cohort is archived, nothing can be added or changed.
 *   - `404 appel_not_found`, `403 permission_denied`.
 */
export type AppelFailure =
  | { kind: "validation"; fields: Record<string, string[]> }
  | { kind: "name_taken" }
  | { kind: "revision_conflict" }
  | { kind: "not_editable" }
  | { kind: "cohort_archived" }
  | { kind: "not_found" }
  | { kind: "forbidden" }
  | { kind: "rate_limited" }
  | { kind: "network" }
  | { kind: "unknown" };

const ValidationBodySchema = z.object({
  fields: z.record(z.string(), z.array(z.object({ code: z.string() }))),
});

export function classifyAppelFailure(error: unknown): AppelFailure {
  if (!isApiError(error)) return { kind: "unknown" };
  if (error.status === 0) return { kind: "network" };
  if (error.status === 429) return { kind: "rate_limited" };
  if (error.status === 403) return { kind: "forbidden" };
  if (error.status === 404) return { kind: "not_found" };

  if (error.status === 409) {
    if (error.code === "name_taken") return { kind: "name_taken" };
    if (error.code === "appel_revision_conflict")
      return { kind: "revision_conflict" };
    if (error.code === "appel_not_editable") return { kind: "not_editable" };
    if (error.code === "cohort_archived") return { kind: "cohort_archived" };
  }

  if (error.status === 400) {
    const parsed = ValidationBodySchema.safeParse(error.body);
    if (parsed.success) {
      const fields: Record<string, string[]> = {};
      for (const [path, issues] of Object.entries(parsed.data.fields)) {
        fields[path] = issues.map((issue) => issue.code);
      }
      return { kind: "validation", fields };
    }
  }

  return { kind: "unknown" };
}
